import React, { useState, useEffect } from 'react';

export type Language = 'FR' | 'EN';

const translations = {
  FR: {
    // Header
    appTitle: 'DiskOver',
    appSubtitle: 'Redécouvrez le monde',
    search: 'Cherchez un lieu à visiter',
    filters: 'Filtres',
    create: 'Créer un podcast',
    
    // Stats
    places: 'Lieux',
    countries: 'Pays',
    
    // Actions
    listenToPodcast: '🎧 Écouter ce podcast',
    seeOnMap: 'Voir sur la carte',
    clearFilters: 'Effacer les filtres',
    seeMore: 'Voir plus',
    
    // Search
    searchPlaceholder: 'Rechercher un lieu, un pays...',
    noResults: 'Aucun résultat trouvé',
    
    // Filter modals
    countriesIn: 'Pays en',
    citiesIn: 'Villes en',
    
    // Loading
    loadingAdventures: 'Chargement des aventures...',
    
    // Errors
    loadingError: 'Problème de connexion',
    loadingErrorDesc: 'Impossible de charger les lieux. Vérifiez votre connexion Internet et actualisez la page.',
    
    // Features
    featureInDev: 'Fonctionnalité en développement',
    featureInDevDesc: 'La création de podcasts sera bientôt disponible !',
    
    // Hero section
    heroTitle: 'Redécouvrez le monde avec vos oreilles',
    heroSubtitle: 'Découvrez le patrimoine culturel mondial à travers des podcasts immersifs de 3 minutes.',
    heroMission: 'Explorez, apprenez et partagez la richesse culturelle de chaque lieu de la planète.',
    exploreWorld: 'Explorer le monde',
    createStory: 'Créer votre histoire',
    
    // Recommendations
    nearbyPlaces: 'Lieux à proximité',
    recommendedForYou: 'Recommandé pour vous',
    trendingNow: 'Tendances actuelles',
    
    // Mission statement
    discoverDifferently: 'Découvrez ce qui vous entoure en 3 minutes',
    missionStatement: 'Écoutez ou créez des podcasts culturels sur des lieux méconnus autour de vous. Pour apprendre, comprendre et explorer librement – même à l\'autre bout du monde.',
    
    // Authentication
    signIn: 'Se connecter',
    signOut: 'Se déconnecter',
    signUp: 'S\'inscrire',
    signedInAs: 'Connecté en tant que',
    authRequired: 'Connexion requise',
    authRequiredDesc: 'Vous devez vous connecter pour créer un podcast.',
    
    // Component translations
    btnListenNearby: 'Écouter un lieu autour de moi',
    btnCreatePodcast: 'Créer un podcast',
    cardInteractiveMap: 'Carte interactive mondiale',
    cardInteractiveMapDesc: 'Découvrez des lieux fascinants sur une carte interactive et fluide',
    cardShortPodcasts: 'Podcasts culturels courts',
    cardShortPodcastsDesc: 'Écoutez des histoires captivantes de 3 minutes dans votre langue',
    cardCollaborative: 'Création collaborative',
    cardCollaborativeDesc: 'Partagez vos découvertes en créant facilement vos propres podcasts',
    startExploring: 'Commencer l\'exploration',
    
    // FAQ
    faqTitle: 'Questions fréquentes sur DiskOver',
    faqWhat: 'Qu\'est-ce que DiskOver ?',
    faqWhatAnswer: 'DiskOver est une plateforme de podcasts culturels qui vous permet de découvrir l\'histoire et la culture de lieux fascinants à travers des podcasts audio courts. Vous pouvez également créer vos propres podcasts et partager vos découvertes culturelles. L\'objectif est de créer la plateforme centralisant l\'intégralité des informations à savoir pour visiter un lieu.',
    faqExplore: 'Comment explorer les podcasts par région ?',
    faqExploreAnswer: 'Chaque podcast est associé à des coordonnées géographiques précises. Grâce à notre carte interactive, vous pouvez explorer visuellement les contenus disponibles près de vous ou dans n\'importe quelle région du monde. Les podcasts s\'affichent sous forme de marqueurs sur la carte.',
    faqLanguages: 'Dans quelles langues sont disponibles les podcasts ?',
    faqLanguagesAnswer: 'Les podcasts sont pour le moment disponibles en français et en anglais uniquement. D\'autres langues arrivent très vite. Vous pouvez facilement basculer entre les langues selon vos préférences.',
    faqCreate: 'Comment créer mon propre podcast sur DiskOver ?',
    faqCreateAnswer: 'Créer un podcast est simple : cliquez sur le bouton "Créer un podcast", recherchez un lieu sur la carte, sélectionnez votre langue, et nous vous créerons un contenu culturel sur celui ci en moins de 1 minute.',
    faqFree: 'Les podcasts DiskOver sont-ils gratuits ?',
    faqFreeAnswer: 'Oui, l\'écoute des podcasts est entièrement gratuite. La création de podcasts nécessite cependant une inscription gratuite qui nous permet de limiter les abus mais surtout de vous offrir plus de fonctionnalités.',
    faqOffline: 'Puis-je utiliser DiskOver hors connexion ?',
    faqOfflineAnswer: 'Les podcasts peuvent être écoutés en streaming uniquement. Pour une utilisation hors connexion, nous travaillons sur une fonctionnalité vous permettant de les télécharger.',
    faqAuth: 'Pourquoi faut-il s\'identifier pour créer des podcasts ?',
    faqAuthAnswer: 'S\'identifier nous permet de suivre les podcasts créés par chaque utilisateur. Afin que, s\'il utilise l\'app à des fins personnelles ou publie sur des lieux non pertinents, je peux intervenir.',
    
    // Podcast Generator
    createPodcastTitle: 'Créer un Podcast',
    choosePlace: 'Choisissez un lieu d\'intérêt et générez automatiquement son podcast',
    generatorSearchPlaceholder: 'Ex: Notre-Dame de Paris',
    placeComplete: 'Lieu complet',
    placeCompleteDesc: 'Des podcasts existent déjà en français et en anglais pour ce lieu.',
    limitedLanguages: 'Langues limitées',
    limitedLanguagesDesc: 'Un podcast existe déjà dans une langue pour ce lieu.',
    podcastLanguage: 'Langue du podcast',
    generating: 'Génération...',
    generatePodcast: 'Générer Podcast',
    podcastInProgress: 'Podcast en cours de création',
    podcastProgressDesc: 'Cela peut prendre jusqu\'à 2 minutes. Vous pouvez quitter cette page, le podcast se lancera automatiquement.',
    errorTitle: 'Erreur',
    retry: 'Réessayer',
    podcastReady: 'Podcast prêt !',
    seeOnMapBtn: 'Voir sur la carte',
    
    // Map
    createPodcastQuestion: 'Voulez-vous créer un podcast sur ce lieu ?',
    cancel: 'Annuler',
    
    // New translations from user
    heroMainTitle: 'Explorez le patrimoine mondial grâce à des podcasts culturels de 3 minutes🔍',
    missionBehind: 'La mission derrière DiskOver',
    quote1: 'Soit il n\'y a aucune information dans notre langue, soit nous passons plus de temps à les lire qu\'à visiter.',
    quote2: 'Ce lieu est sublime. Mais... quelle est la différence avec les deux précédents ?',
    quote3: 'Et si chaque lieu nous parlait ? En 3 minutes. Dans notre langue.',
    missionPurpose: 'Tel est le but de ce projet : faciliter la découverte culturelle, partager la culture mondiale, mais surtout mieux promouvoir nos patrimoines et favoriser l\'apprentissage et la découverte plutôt que les selfies.',
    
    // Search and navigation
    searchPlaceExample: 'Rechercher un lieu (ex: Musée du Louvre, Paris)',
    backToHome: 'Retour à l\'accueil',
    createNewPodcast: 'Créer un nouveau podcast culturel',
    noResultsFor: 'Aucun résultat trouvé pour',
    
    // Map and geolocation
    createPodcastFrench: 'Créer un podcast en français',
    createPodcastEnglish: 'Créer un podcast en anglais',
    podcastAvailableFrench: 'Podcast disponible en français',
    
    // Errors and notifications
    geolocationError: 'Localisation indisponible',
    unableGetLocation: 'Impossible d\'obtenir votre position. Activez la géolocalisation dans les paramètres de votre navigateur.',
    placeNotFound: 'Lieu introuvable',
    placeNotExist: 'Ce lieu n\'existe pas encore dans notre collection.',
    unableLoadPlace: 'Impossible de charger les informations de ce lieu.',
    informationUpdated: 'Informations actualisées',
    placeInfoUpdated: 'Les informations du lieu ont été mises à jour.',
    unableRetrieveInfo: 'Impossible de récupérer les informations depuis le site officiel.',
    podcastCreated: 'Podcast créé avec succès !',
    newPodcastCreated: 'Votre nouveau podcast culturel est maintenant disponible.',
    
    // Report errors
    reportError: 'Reporter une erreur',
    reportErrorDesc: 'Aidez-nous à améliorer ce podcast en signalant les problèmes rencontrés.',
    problemTypes: 'Types de problèmes :',
    audioBug: 'Bug dans l\'audio',
    wrongInfo: 'Informations erronées',
    poorAudioQuality: 'Qualité audio insuffisante',
    wrongLocation: 'Mauvais lieu présenté',
    wrongImage: 'Mauvaise image',
    wrongDescription: 'Mauvaise description',
    validate: 'Valider',
    sending: 'Envoi...',
    thankYouFeedback: 'Merci pour votre retour',
    selectErrorType: 'Veuillez sélectionner au moins un type d\'erreur ou saisir un commentaire.',
    improveExperience: 'et de nous permettre d\'améliorer l\'expérience de tous les utilisateurs.',
    errorSendingReport: 'Une erreur s\'est produite lors de l\'envoi du rapport. Veuillez réessayer.',
    describeProblem: 'Décrivez le problème en quelques mots',
    
    // Place details
    keyInformation: 'Informations clés',
    
    // FAQ new questions
    faqLanguagesNew: 'Dans quelles langues sont disponibles les podcasts ?',
    faqCreateNew: 'Comment créer mon propre podcast sur DiskOver ?',
    faqFreeNew: 'Les podcasts DiskOver sont-ils gratuits ?',
    faqOfflineNew: 'Puis-je utiliser DiskOver hors connexion ?',
    faqAuthNew: 'Pourquoi faut-il s\'identifier pour créer des podcasts ?',
    
    // FAQ new answers
    faqLanguagesAnswerNew: 'Les podcasts sont pour le moment disponibles en français et en anglais uniquement. D\'autres langues arrivent très vite. Vous pouvez facilement basculer entre les langues selon vos préférences. Certains lieux peuvent avoir des contenus dans une langue seulement, mais nous encourageons la création de versions multilingues.',
    faqCreateAnswerNew: 'Créer un podcast est simple : cliquez sur le bouton "Créer un podcast", recherchez un lieu sur la carte, sélectionnez votre langue, et nous vous crérons un contenu culturel sur celui ci en moins de 1 minute. Et permettre à tous de découvrir ce lieu.',
    faqFreeAnswerNew: 'Oui, l\'écoute des podcasts est entièrement gratuite. La création de podcasts nécessite cependant une inscription gratuite qui nous permet de limiter les abus mais surtout de vous offrir plus de fonctionnalités. Notre mission est de rendre la culture accessible à tous.',
    faqOfflineAnswerNew: 'Les podcasts peuvent être écoutés en streaming uniquement. Pour une utilisation hors connexion, nous travaillons sur une fonctionnalitée vous permettant de les télécharger. La carte interactive nécessite une connexion internet pour fonctionner optimalement.',
    faqAuthAnswerNew: 'S\'identifier nous permet de suivre les podcasts créés par chaque utilisateur. Afin que, s\'il utilise l\'app à des fins personnelles ou publie sur des lieux non pertinents, je peux intervenir. Le but, etant de garantir une expérience enrichissante pour toute la communauté.',
    
    // Footer and UI
    madeWithLove: 'Fait avec ❤️',
    culturalExploration: 'pour l\'exploration culturelle',
    sitemap: 'Plan du site',
    legalNotice: 'Mentions légales',
    explorePodcasts: 'Explorer les podcasts',
    logIn: 'Connexion',
    loadingDiscoveries: 'Chargement des découvertes...',
    noPlaceFound: 'Aucun lieu trouvé pour',
    createPodcastForPlace: 'Créer un podcast pour ce lieu',
    
    // Audio and buttons
    listen: 'Écouter',
    createPlus: 'Créer +',
    createThisPodcast: 'Créer ce podcast',
    createAccount: 'Créer un compte',
    french: 'français',
    english: 'anglais',
    
    // User interface
    userProfileMenu: 'Menu du profil utilisateur',
    user: 'Utilisateur',
    
    // Messages and notifications
    howGetMapboxToken: 'Comment obtenir un token Mapbox :',
    
    // Legal pages
    legalNoticeTitle: 'Mentions Légales',
    personalDataProtection: 'Protection des Données Personnelles',
    diskoverCommitment: 'DiskOver s\'engage à protéger la confidentialité des informations personnelles des utilisateurs.',
    
    // SEO JSON-LD
    whatIsDiskover: 'Qu\'est-ce que DiskOver ?',
    howExplorePodcasts: 'Comment explorer les podcasts par région ?',
    diskoverDescription: 'DiskOver est une plateforme de podcasts culturels qui vous permet de découvrir l\'histoire et la culture de lieux fascinants à travers des histoires audio courtes de 3 minutes.',
    
    // Continent translations
    continentEurope: 'Europe',
    continentAsia: 'Asie', 
    continentAfrica: 'Afrique',
    continentNorthAmerica: 'Amérique du Nord',
    continentSouthAmerica: 'Amérique du Sud',
    continentOceania: 'Océanie',
    continentAntarctica: 'Antarctique',

    // Additional UI texts
    discoverWorld: 'Découvrir le monde',
    recommendedAround: 'Recommandés autour de vous',
    aroundMe: 'Autour de moi',
    placesDisplayed: 'lieux affichés',
    locateMe: 'Me localiser',
    
    // Place Details Page
    loadingPlace: 'Chargement des informations...',
    backButton: 'Retour',
    quickActions: 'Actions rapides',
    siteLabel: 'Site',
    addressLabel: 'Adresse', 
    phoneLabel: 'Numéro',
    aboutTitle: 'À propos',
    practicalInfoTitle: 'Informations pratiques',
    scheduleLabel: 'Horaires',
    pricingLabel: 'Tarifs',
    visitTipsTitle: 'Conseils de visite',
    infoCollectionTitle: 'Informations en cours de collecte',
    infoCollectionDesc: 'Nous enrichissons constamment notre base de données avec plus d\'informations pratiques.',
    refreshInfoButton: 'Récupérer les informations',
    noOfficialSite: 'Site officiel non disponible pour ce lieu',
    
    // Audio Section
    podcastComingSoon: 'Podcast bientôt disponible',
    podcastComingSoonDesc: 'Nous préparons un podcast exclusif pour ce lieu culturel',
    podcastAudio: 'Podcast Audio',
    podcastAudioDesc: 'Découvrez ce lieu à travers nos récits',
    frenchVersion: 'Version française',
    englishVersion: 'Version anglaise',
    podcastNotCreated: 'Podcast pas encore créé',
    createButton: 'Créer +',
    
    // Additional Place Details translations
    placeNotFoundDesc: 'Ce lieu n\'existe pas encore dans notre collection de podcasts culturels.',
    loadingPlaceError: 'Impossible de charger les informations de ce lieu. Vérifiez votre connexion.',
    missingOfficialSite: 'Site officiel non disponible',
    missingOfficialSiteDesc: 'Aucun site officiel n\'est référencé pour ce lieu.',
    infoUpdated: 'Informations actualisées',
    infoUpdatedDesc: 'Les informations pratiques ont été mises à jour.',
    scrapingError: 'Impossible de récupérer les informations depuis le site officiel.',
    podcastCreatedDesc: 'Votre nouveau podcast culturel est maintenant disponible à l\'écoute.',
    
    // Sitemap page
    staticPages: 'Pages Statiques',
    blogPosts: 'Articles de Blog',
    toursPage: 'Tours',
    placesPage: 'Lieux',
    toursAvailable: 'tours disponibles',
    
    // Tours
    mustDo: 'Must Do',
    bestRated: 'Mieux Notés', 
    offTheBeatenPath: 'Hors des Sentiers Battus',
    mustDoDescription: 'Lieux emblématiques incontournables de la ville',
    bestRatedDescription: 'Lieux populaires avec d\'excellents avis',
    offTheBeatenPathDescription: 'Découvertes authentiques et locales',
    customTour: 'Tour personnalisé',
    viewOnMap: 'Voir sur la carte',
    details: 'Détails',
    
    // Tour Page
    tourPageTitle: 'Tour d\'une journée de',
    tourPageHalfDay: 'demi-journée',
    tourPageFullDay: 'journée complète',
    tourPageIntro: 'Avec notre équipe, nous avons créé un tour avec la présentation des lieux que vous ne devez pas manquer.',
    tourPageAbout: 'À propos',
    tourPageVisitTips: 'Conseils de visite',
    tourPageDiscoverMore: 'Découvrir plus sur',
    tourPageConclusion: 'N\'hésitez pas à sortir des chemins classiques et à explorer ! Faites-nous des retours si notre tour nécessite des améliorations.',
    tourPageStyleMustDo: 'voir les incontournables',
    tourPageStyleOffBeaten: 'fuir les zones touristiques',
    tourPageStyleBalanced: 'le meilleur mix entre sites emblématiques et lieux authentiques',
    tourPageStyleBestRated: 'découvrir les mieux notés',
    tourPageViewFullPage: 'Voir la page complète du tour',
  },
  EN: {
    // Header
    appTitle: 'DiskOver',
    appSubtitle: 'Rediscover the world',
    search: 'Search',
    filters: 'Filters',
    create: 'Create a podcast',
    
    // Stats
    places: 'Places',
    countries: 'Countries',
    
    // Actions
    listenToPodcast: '🎧 Listen to this podcast',
    seeOnMap: 'See on map',
    clearFilters: 'Clear filters',
    seeMore: 'See more',
    
    // Search
    searchPlaceholder: 'Search for a place, country...',
    noResults: 'No results found',
    
    // Filter modals
    countriesIn: 'Countries in',
    citiesIn: 'Cities in',
    
    // Loading
    loadingAdventures: 'Loading adventures...',
    
    // Errors
    loadingError: 'Loading error',
    loadingErrorDesc: 'Unable to load podcasts. Please try again.',
    
    // Features
    featureInDev: 'Feature in development',
    featureInDevDesc: 'Podcast creation will be available soon!',
    
    // Hero section
    heroTitle: 'Rediscover the world with your ears',
    heroSubtitle: 'Discover global cultural heritage through immersive 3-minute podcasts.',
    heroMission: 'Explore, learn and share the cultural richness of every place on the planet.',
    exploreWorld: 'Explore the world',
    createStory: 'Create your story',
    
    // Recommendations
    nearbyPlaces: 'Nearby places',
    recommendedForYou: 'Recommended for you',
    trendingNow: 'Trending now',
    
    // Mission statement
    discoverDifferently: 'Discover your surroundings in 3 minutes',
    missionStatement: 'Listen to or create cultural podcasts about little-known places around you. Learn, understand and explore freely - even on the other side of the world.',
    
    // Authentication
    signIn: 'Sign in',
    signOut: 'Sign out',
    signUp: 'Sign up',
    signedInAs: 'Signed in as',
    authRequired: 'Authentication required',
    authRequiredDesc: 'You need to sign in to create a podcast.',
    
    // Component translations
    btnListenNearby: 'Listen to a place around me',
    btnCreatePodcast: 'Create a podcast',
    cardInteractiveMap: 'Global interactive map',
    cardInteractiveMapDesc: 'Discover fascinating places on an interactive and fluid map',
    cardShortPodcasts: 'Short cultural podcasts',
    cardShortPodcastsDesc: 'Listen to captivating 3-minute stories in your language',
    cardCollaborative: 'Collaborative creation',
    cardCollaborativeDesc: 'Share your discoveries by easily creating your own podcasts',
    startExploring: 'Start exploring',
    
    // FAQ
    faqTitle: 'Frequently asked questions about DiskOver',
    faqWhat: 'What is DiskOver?',
    faqWhatAnswer: 'DiskOver is a cultural podcast platform that allows you to discover the history and culture of fascinating places through short audio podcasts. You can also create your own podcasts and share your cultural discoveries. The goal is to create the platform centralizing all the information you need to know to visit a place.',
    faqExplore: 'How to explore podcasts by region?',
    faqExploreAnswer: 'Each podcast is associated with precise geographical coordinates. Thanks to our interactive map, you can visually explore content available near you or anywhere in the world. Podcasts are displayed as markers on the map.',
    faqLanguages: 'In which languages are podcasts available?',
    faqLanguagesAnswer: 'Podcasts are currently available in French and English only. Other languages are coming very soon. You can easily switch between languages according to your preferences.',
    faqCreate: 'How to create my own podcast on DiskOver?',
    faqCreateAnswer: 'Creating a podcast is simple: click the "Create a podcast" button, search for a place on the map, select your language, and we will create cultural content about it in less than 1 minute.',
    faqFree: 'Are DiskOver podcasts free?',
    faqFreeAnswer: 'Yes, listening to podcasts is completely free. Creating podcasts requires a free registration that allows us to limit abuse but above all to offer you more features.',
    faqOffline: 'Can I use DiskOver offline?',
    faqOfflineAnswer: 'Podcasts can only be listened to in streaming. For offline use, we are working on a feature allowing you to download them.',
    faqAuth: 'Why do I need to identify myself to create podcasts?',
    faqAuthAnswer: 'Identifying allows us to track podcasts created by each user. So if someone uses the app for personal purposes or publishes on irrelevant places, I can intervene.',
    
    // Podcast Generator
    createPodcastTitle: 'Create a Podcast',
    choosePlace: 'Choose a place of interest and automatically generate its podcast',
    generatorSearchPlaceholder: 'Ex: Notre-Dame de Paris',
    placeComplete: 'Place complete',
    placeCompleteDesc: 'Podcasts already exist in French and English for this place.',
    limitedLanguages: 'Limited languages',
    limitedLanguagesDesc: 'A podcast already exists in one language for this place.',
    podcastLanguage: 'Podcast language',
    generating: 'Generating...',
    generatePodcast: 'Generate Podcast',
    podcastInProgress: 'Podcast being created',
    podcastProgressDesc: 'This can take up to 2 minutes. You can leave this page, the podcast will start automatically.',
    errorTitle: 'Error',
    retry: 'Retry',
    podcastReady: 'Podcast ready!',
    seeOnMapBtn: 'See on map',
    
    // Map
    createPodcastQuestion: 'Do you want to create a podcast about this place?',
    cancel: 'Cancel',
    
    // New translations from user
    heroMainTitle: 'Explore world heritage through 3-minute cultural podcasts🔍',
    missionBehind: 'The mission behind DiskOver',
    quote1: 'Either there is no information in our language, or we spend more time reading it than visiting.',
    quote2: 'This place is stunning. But… what\'s the difference from the last two?',
    quote3: 'What if every place could speak to us? In 3 minutes. In our language.',
    missionPurpose: 'That is the purpose of this project: to make cultural discovery easier, to share global culture, but above all to better promote our heritage and encourage learning and discovery rather than selfies.',
    
    // Search and navigation
    searchPlaceExample: 'Search for a place (e.g.: Louvre Museum, Paris)',
    backToHome: 'Back to home',
    createNewPodcast: 'Create a new cultural podcast',
    noResultsFor: 'No results found for',
    
    // Map and geolocation
    createPodcastFrench: 'Create a podcast in French',
    createPodcastEnglish: 'Create a podcast in English',
    podcastAvailableFrench: 'Podcast available in French',
    
    // Errors and notifications
    geolocationError: 'Geolocation error',
    unableGetLocation: 'Unable to get your location. Please check your location settings.',
    placeNotFound: 'Place not found',
    placeNotExist: 'This place does not exist in our database.',
    unableLoadPlace: 'Unable to load place data.',
    informationUpdated: 'Information updated',
    placeInfoUpdated: 'The place information has been successfully updated.',
    unableRetrieveInfo: 'Unable to retrieve information from the official website.',
    podcastCreated: 'Podcast created!',
    newPodcastCreated: 'The new podcast has been successfully created.',
    
    // Report errors
    reportError: 'Report an error',
    reportErrorDesc: 'Help us improve this podcast by reporting issues encountered.',
    problemTypes: 'Problem types:',
    audioBug: 'Audio bug',
    wrongInfo: 'Incorrect information',
    poorAudioQuality: 'Poor audio quality',
    wrongLocation: 'Wrong location presented',
    wrongImage: 'Wrong image',
    wrongDescription: 'Wrong description',
    validate: 'Submit',
    sending: 'Sending...',
    thankYouFeedback: 'Thank you for your feedback',
    selectErrorType: 'Please select at least one type of error or enter a comment.',
    improveExperience: 'and allow us to improve the experience for all users.',
    errorSendingReport: 'An error occurred while sending the report. Please try again.',
    describeProblem: 'Describe the problem in a few words',
    
    // Place details
    keyInformation: 'Key information',
    
    // FAQ new questions
    faqLanguagesNew: 'In which languages are the podcasts available?',
    faqCreateNew: 'How can I create my own podcast on DiskOver?',
    faqFreeNew: 'Are DiskOver podcasts free?',
    faqOfflineNew: 'Can I use DiskOver offline?',
    faqAuthNew: 'Why do I need to sign in to create podcasts?',
    
    // FAQ new answers
    faqLanguagesAnswerNew: 'Podcasts are currently available in French and English only. More languages are coming soon. You can easily switch between languages according to your preferences. Some places may only have content in one language, but we encourage the creation of multilingual versions.',
    faqCreateAnswerNew: 'Creating a podcast is simple: click on the \'Create a podcast\' button, search for a place on the map, select your language, and we will create cultural content for it in less than one minute. This allows everyone to discover that place.',
    faqFreeAnswerNew: 'Yes, listening to podcasts is completely free. However, creating podcasts requires a free registration, which helps us limit abuse but above all allows us to offer you more features. Our mission is to make culture accessible to everyone.',
    faqOfflineAnswerNew: 'Podcasts can currently be listened to via streaming only. For offline use, we are working on a feature that will allow you to download them. The interactive map requires an internet connection to function optimally.',
    faqAuthAnswerNew: 'Signing in allows us to track the podcasts created by each user, so that if they use the app for personal purposes or publish content on irrelevant places, we can intervene. The goal is to ensure an enriching experience for the whole community.',
    
    // Footer and UI
    madeWithLove: 'Made with ❤️',
    culturalExploration: 'for cultural exploration',
    sitemap: 'Sitemap',
    legalNotice: 'Legal notice',
    explorePodcasts: 'Explore podcasts',
    logIn: 'Log in',
    loadingDiscoveries: 'Loading discoveries...',
    noPlaceFound: 'No place found for',
    createPodcastForPlace: 'Create a podcast for this place',
    
    // Audio and buttons
    listen: 'Listen',
    createPlus: 'Create +',
    createThisPodcast: 'Create this podcast',
    createAccount: 'Create an account',
    french: 'French',
    english: 'English',
    
    // User interface
    userProfileMenu: 'User profile menu',
    user: 'User',
    
    // Messages and notifications
    howGetMapboxToken: 'How to get a Mapbox token:',
    
    // Legal pages
    legalNoticeTitle: 'Legal Notice',
    personalDataProtection: 'Personal Data Protection',
    diskoverCommitment: 'DiskOver is committed to protecting the privacy of users\' personal information.',
    
    // SEO JSON-LD
    whatIsDiskover: 'What is DiskOver?',
    howExplorePodcasts: 'How to explore podcasts by region?',
    diskoverDescription: 'DiskOver is a cultural podcast platform that allows you to discover the history and culture of fascinating places through short 3-minute audio stories.',
    
    // Continent translations
    continentEurope: 'Europe',
    continentAsia: 'Asia', 
    continentAfrica: 'Africa',
    continentNorthAmerica: 'North America',
    continentSouthAmerica: 'South America',
    continentOceania: 'Oceania',
    continentAntarctica: 'Antarctica',

    // Additional UI texts
    discoverWorld: 'Discover the world',
    recommendedAround: 'Recommended around you',
    aroundMe: 'Around me',
    placesDisplayed: 'places displayed',
    locateMe: 'Locate me',
    
    // Place Details Page
    loadingPlace: 'Loading information...',
    backButton: 'Back',
    quickActions: 'Quick actions',
    siteLabel: 'Site',
    addressLabel: 'Address',
    phoneLabel: 'Phone',
    aboutTitle: 'About',
    practicalInfoTitle: 'Practical information',
    scheduleLabel: 'Schedule',
    pricingLabel: 'Pricing',
    visitTipsTitle: 'Visit tips',
    infoCollectionTitle: 'Information being collected',
    infoCollectionDesc: 'We are constantly enriching our database with more practical information.',
    refreshInfoButton: 'Get information',
    noOfficialSite: 'Official website not available for this place',
    
    // Audio Section
    podcastComingSoon: 'Podcast coming soon',
    podcastComingSoonDesc: 'We are preparing an exclusive podcast for this cultural site',
    podcastAudio: 'Audio Podcast',
    podcastAudioDesc: 'Discover this place through our stories',
    frenchVersion: 'French version',
    englishVersion: 'English version',
    podcastNotCreated: 'Podcast not yet created',
    createButton: 'Create +',
    
    // Additional Place Details translations
    placeNotFoundDesc: 'This place does not exist in our database.',
    loadingPlaceError: 'Unable to load place data.',
    missingOfficialSite: 'Missing official website',
    missingOfficialSiteDesc: 'No official website is available for this place.',
    infoUpdated: 'Information updated',
    infoUpdatedDesc: 'The place information has been successfully updated.',
    scrapingError: 'Unable to retrieve information from the official website.',
    podcastCreatedDesc: 'The new podcast has been successfully generated.',
    
    // Sitemap page
    staticPages: 'Static Pages',
    blogPosts: 'Blog Posts',
    toursPage: 'Tours',
    placesPage: 'Places',
    toursAvailable: 'tours available',
    
    // Tours
    mustDo: 'Must Do',
    bestRated: 'Best Rated',
    offTheBeatenPath: 'Off the Beaten Path',
    mustDoDescription: 'Iconic must-see places in the city',
    bestRatedDescription: 'Popular places with excellent reviews',
    offTheBeatenPathDescription: 'Authentic and local discoveries',
    customTour: 'Custom tour',
    viewOnMap: 'View on map',
    details: 'Details',
    
    // Tour Page
    tourPageTitle: 'One day tour of',
    tourPageHalfDay: 'half-day',
    tourPageFullDay: 'full day',
    tourPageIntro: 'With our team, we created a tour with the presentation of the places you must not miss.',
    tourPageAbout: 'About',
    tourPageVisitTips: 'Visit tips',
    tourPageDiscoverMore: 'Discover more about',
    tourPageConclusion: 'Don\'t hesitate to venture off the beaten path and explore! Send us your feedback if our tour needs improvements.',
    tourPageStyleMustDo: 'see the must-see attractions',
    tourPageStyleOffBeaten: 'escape the tourist crowds',
    tourPageStyleBalanced: 'the best mix of iconic sites and authentic places',
    tourPageStyleBestRated: 'discover the best rated',
    tourPageViewFullPage: 'View full tour page',
  }
};

export const useTranslation = () => {
  const [language, setLanguage] = useState<Language>(() => {
    // Get language from domain first, then localStorage
    const hostname = window.location.hostname;
    if (hostname.includes('fr.diskover.ovh')) {
      return 'FR';
    }
    
    // Fallback to localStorage
    const saved = localStorage.getItem('language');
    if (saved === 'FR' || saved === 'EN') {
      return saved;
    }
    return 'EN'; // Default to English
  });
  
  const t = (key: keyof typeof translations.FR): string => {
    return translations[language][key] || key;
  };
  
  // Function to translate continent names
  const translateContinent = (continent: string): string => {
    const continentMap: Record<string, keyof typeof translations.FR> = {
      'Europe': 'continentEurope',
      'Asie': 'continentAsia',
      'Asia': 'continentAsia',
      'Afrique': 'continentAfrica', 
      'Africa': 'continentAfrica',
      'Amérique du Nord': 'continentNorthAmerica',
      'North America': 'continentNorthAmerica',
      'Amérique du Sud': 'continentSouthAmerica',
      'South America': 'continentSouthAmerica',
      'Océanie': 'continentOceania',
      'Oceania': 'continentOceania',
      'Antarctique': 'continentAntarctica',
      'Antarctica': 'continentAntarctica',
    };
    
    const translationKey = continentMap[continent];
    return translationKey ? t(translationKey) : continent;
  };
  
  const toggleLanguage = () => {
    setLanguage(prev => {
      const newLang = prev === 'FR' ? 'EN' : 'FR';
      // Save to localStorage for persistence
      localStorage.setItem('language', newLang);
      
      // Switch domains: www.diskover.ovh (EN) <-> fr.diskover.ovh (FR)
      const path = window.location.pathname + window.location.search;
      
      if (newLang === 'FR') {
        window.location.href = `https://fr.diskover.ovh${path}`;
      } else {
        window.location.href = `https://www.diskover.ovh${path}`;
      }
      
      return newLang;
    });
  };
  
  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);
  
  return {
    language,
    setLanguage,
    toggleLanguage,
    t,
    translateContinent
  };
};